// frontend/src/components/Test/FotoCaptura.js
//
// Pantalla de verificación de identidad: se muestra una sola vez, antes de
// las instrucciones generales, y toma una fotografía del candidato con su
// cámara. No se puede volver a mostrar una vez que la foto ya fue enviada
// (el backend también lo garantiza).

import React, { useState, useRef, useEffect, useCallback } from 'react';
import testService from '../../services/testService';

const FotoCaptura = ({ token, onCompletado }) => {
  const [aceptoAviso, setAceptoAviso] = useState(false);
  const [camaraActiva, setCamaraActiva] = useState(false);
  const [videoListo, setVideoListo] = useState(false);
  const [errorCamara, setErrorCamara] = useState(null);
  const [fotoDataUrl, setFotoDataUrl] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const detenerCamara = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => detenerCamara();
  }, [detenerCamara]);

  // El elemento <video> solo se monta cuando camaraActiva pasa a true, así
  // que asignar srcObject dentro de activarCamara no sirve: en ese momento
  // el ref todavía apunta a null. Se asigna aquí, una vez montado, y se
  // llama a play() explícitamente en vez de depender solo del atributo
  // autoPlay (poco confiable al asignar srcObject de forma imperativa).
  useEffect(() => {
    if (camaraActiva && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [camaraActiva]);

  const activarCamara = async () => {
    setErrorCamara(null);
    setVideoListo(false);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 480 }, height: { ideal: 360 } },
        audio: false
      });
      streamRef.current = stream;
      setCamaraActiva(true);
    } catch (err) {
      console.error('Error al activar la cámara:', err);
      setErrorCamara('No se pudo acceder a tu cámara. Revisa los permisos del navegador e inténtalo de nuevo, o continúa sin fotografía.');
    }
  };

  const tomarFoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setFotoDataUrl(dataUrl);
    detenerCamara();
    setCamaraActiva(false);
  };

  const repetirFoto = () => {
    setFotoDataUrl(null);
    setErrorEnvio(null);
    activarCamara();
  };

  const confirmarFoto = async () => {
    setEnviando(true);
    setErrorEnvio(null);
    try {
      await testService.subirFoto(token, fotoDataUrl);
      onCompletado();
    } catch (err) {
      setErrorEnvio(err.error || 'No se pudo guardar tu fotografía. Inténtalo de nuevo.');
      setEnviando(false);
    }
  };

  const continuarSinFoto = () => {
    detenerCamara();
    onCompletado();
  };

  return (
    <div className="test-instructions-container">
      <div className="instructions-card foto-captura-card">
        <img src="/Logotransparente.png" alt="Logo" className="logo-img" />
        <h1>Verificación de identidad</h1>

        <div className="foto-aviso">
          <p>
            Antes de comenzar, te pediremos tomarte una fotografía con tu cámara. Esta imagen se usa
            únicamente para verificar tu identidad durante el proceso de selección y se almacena de
            forma privada junto con tus resultados.
          </p>
          <p className="foto-aviso-privacidad">
            <strong>Aviso de privacidad:</strong> tu fotografía se conservará mientras dure el proceso de
            selección en el que participas. Puedes solicitar en cualquier momento la eliminación de tus
            datos personales escribiendo a <a href="mailto:contacto@doublecoding.com">contacto@doublecoding.com</a>.
          </p>
        </div>

        <div className="foto-consentimiento">
          <label className="foto-checkbox-label">
            <input type="checkbox" checked={aceptoAviso} onChange={(e) => setAceptoAviso(e.target.checked)} />
            He leído el aviso de privacidad y acepto que se me tome una fotografía con fines de verificación.
          </label>
        </div>

        {aceptoAviso && (
          <div className="foto-captura-area">
            {!fotoDataUrl && !camaraActiva && (
              <div className="foto-captura-inicio">
                <button className="btn-start" onClick={activarCamara}>Activar cámara</button>
              </div>
            )}

            {errorCamara && (
              <div className="foto-error">
                <p>{errorCamara}</p>
                <div className="foto-error-acciones">
                  <button className="btn-start" onClick={activarCamara}>Reintentar</button>
                  <button className="btn-secundario-foto" onClick={continuarSinFoto}>Continuar sin fotografía</button>
                </div>
              </div>
            )}

            {camaraActiva && !fotoDataUrl && (
              <div className="foto-preview-wrap">
                <div className="foto-video-frame">
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="foto-video"
                    onLoadedMetadata={() => setVideoListo(true)}
                  />
                  <svg className="foto-guia-silueta" viewBox="0 0 300 300" preserveAspectRatio="xMidYMid slice">
                    <path d="
                      M150,60
                      C121,60 100,84 100,116
                      C100,148 121,172 150,172
                      C179,172 200,148 200,116
                      C200,84 179,60 150,60
                      Z
                      M60,300
                      C60,215 98,190 150,190
                      C202,190 240,215 240,300
                    " />
                  </svg>
                </div>
                <p className="foto-guia-texto">Centra tu rostro y hombros dentro de la silueta</p>
                <button className="btn-start" onClick={tomarFoto} disabled={!videoListo}>
                  {videoListo ? 'Tomar fotografía' : 'Preparando cámara...'}
                </button>
              </div>
            )}

            {fotoDataUrl && (
              <div className="foto-preview-wrap">
                <img src={fotoDataUrl} alt="Vista previa" className="foto-video" />
                {errorEnvio && <p className="foto-error-mensaje">{errorEnvio}</p>}
                <div className="foto-confirmar-acciones">
                  <button className="btn-secundario-foto" onClick={repetirFoto} disabled={enviando}>Repetir</button>
                  <button className="btn-start" onClick={confirmarFoto} disabled={enviando}>
                    {enviando ? 'Guardando...' : 'Confirmar y continuar'}
                  </button>
                </div>
              </div>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        )}
      </div>
    </div>
  );
};

export default FotoCaptura;
